import { LoadingButton } from '@/components/common/LoadingButton';
import { MuiDateRangePicker } from '@/components/common/MuiDatePicker';
import { getSelectEndTimeList, getSelectStartTimeList } from '@/components/common/MuiDatePicker/selecttimeList';
import { formatLocaleDate } from '@/utils/formatter';
import { Cancel, Search } from '@mui/icons-material';
import {
    Box,
    Divider,
    FormControl,
    Unstable_Grid2 as Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    styled,
} from '@mui/material';
import { find } from 'lodash';
import moment from 'moment-timezone';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigation, useRevalidator } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface PropType {
    contractList?: any[];
    filters: Record<string, any>;
    onSubmit?: (filters: Record<string, any>) => void;
}

const StyledToggleBtn = styled(ToggleButton)(() => ({
    '&.Mui-selected, &.Mui-selected:hover': {
        backgroundColor: '#ff6aac',
    },
}));

const SalesListFilters = (props: PropType) => {
    const { state } = useNavigation();
    const { revalidate } = useRevalidator();
    const { t } = useTranslation();
    const { contractList = [], filters = {}, onSubmit = () => {} } = props;
    const form = useForm<Record<string, any>>();
    const formValues = form.getValues();
    const {
        watch,
        reset,
        setValue,
        getValues,
        register,
        formState: { isDirty },
    } = form;
    const salesTypeList = [
        { key: 'contract', value: t('mall.salesByGroupPurchase') },
        { key: 'date', value: t('mall.salesByPeriod') },
    ];
    const staticPeriodList = [
        { key: 'yesterday', value: t('mall.yesterday') },
        { key: 'lastWeek', value: t('mall.lastWeek') },
        { key: 'last2Week', value: t('mall.last2Weeks') },
        { key: 'lastMonth', value: t('mall.lastMonth') },
        { key: 'last3Month', value: t('mall.last3Months') },
        { key: 'last6Month', value: t('mall.last6Months') },
        // { key: 'last12Month', value: '1년' },
    ];
    const periodTypeList = [
        { key: 'DAY', value: t('mall.byDay') },
        { key: 'MONTH', value: t('mall.byMonth') },
    ];
    const ownerTypeList = [
        { key: 'whole', value: t('mall.allProducts') },
        { key: 'VENDOR', value: t('mall.groupPurchaseProducts') },
        { key: 'SELLER', value: t('mall.myProducts') },
    ];
    const selectableMonthList = useMemo(() => generateMonthlyOptions(t), [t]);
    useEffect(() => {
        reset({
            salesType: filters['salesType'] || 'contract',
            contractId: filters['contractId'] || '',
            sellerId: filters['sellerId'] || '',
            period: filters['period'] || 'DAY',
            startDate: filters['startDate'] || monthDefaultStart,
            endDate: filters['endDate'] || monthDefaultEnd,
            staticPeriod: filters['staticPeriod'] || 'lastMonth',
            ownerType: filters['ownerType'] || 'whole',
            startMonth: filters['startMonth'] || defaultStartMonth,
            endMonth: filters['endMonth'] || defaultEndMonth,
        });
    }, [filters]);
    const { salesType, contractId, period, startDate, endDate, staticPeriod, ownerType, startMonth, endMonth } =
        watch();
    const submit = (action?: string) => {
        if (!isDirty) return;
        onSubmit({
            salesType: formValues?.salesType || 'contract',
            contractId: !!action && action === 'contractIdReset' ? '' : formValues?.contractId || '',
            sellerId: formValues?.sellerId || '',
            period: formValues?.period || 'DAY',
            startDate: formValues?.startDate || new Date(new Date().getTime() - 86400000 * 30).toISOString(),
            endDate: formValues?.endDate || new Date().toISOString(),
            staticPeriod: formValues?.staticPeriod || 'yesterday',
            ownerType: formValues?.ownerType || 'whole',
            startMonth: formValues?.startMonth || defaultStartMonth,
            endMonth: formValues?.endMonth || defaultEndMonth,
        });
    };
    return (
        <>
            <Grid container>
                <Grid xs={12} lg={0.5} minWidth={80}>
                    <Box className="flex w-full justify-center items-center mt-4">
                        <Typography>{t('mall.salesType')}</Typography>
                    </Box>
                </Grid>
                <Grid xs={12} lg={2}>
                    <ToggleButtonGroup
                        exclusive
                        fullWidth
                        size="small"
                        sx={{ m: 0, p: 0, pl: 1.5 }}
                        color="primary"
                        onChange={(e, v) => {
                            if (!v) return;
                            setValue('salesType', v, { shouldDirty: true });
                        }}
                        value={salesType}
                    >
                        {salesTypeList.map((item) => {
                            return (
                                <StyledToggleBtn color="primary" value={item.key} key={item.key}>
                                    <Typography
                                        textTransform={'none'}
                                        fontWeight={500}
                                        color={salesType === item.key ? '#fff' : undefined}
                                    >
                                        {item.value}
                                    </Typography>
                                </StyledToggleBtn>
                            );
                        })}
                    </ToggleButtonGroup>
                </Grid>
                {salesType === 'date' && (
                    <>
                        <Grid xs={12} lg={2}>
                            <ToggleButtonGroup
                                exclusive
                                fullWidth
                                color="primary"
                                onChange={(e, v) => {
                                    if (!v) return;
                                    setValue('period', v, { shouldDirty: true });
                                }}
                                size="small"
                                value={period}
                                sx={{ mb: 2 }}
                            >
                                {periodTypeList.map((item) => {
                                    return (
                                        <StyledToggleBtn color="primary" value={item.key} key={item.key}>
                                            <Typography
                                                fontWeight={500}
                                                textTransform={'none'}
                                                color={period === item.key ? '#fff' : undefined}
                                            >
                                                {item.value}
                                            </Typography>
                                        </StyledToggleBtn>
                                    );
                                })}
                            </ToggleButtonGroup>
                        </Grid>
                        <Grid xs={12} lg={2}>
                            <ToggleButtonGroup
                                exclusive
                                fullWidth
                                color="primary"
                                onChange={(e, v) => {
                                    if (!v) return;
                                    setValue('ownerType', v, { shouldDirty: true });
                                }}
                                size="small"
                                value={ownerType}
                                sx={{ mb: 2 }}
                            >
                                {ownerTypeList.map((item) => {
                                    return (
                                        <StyledToggleBtn color="primary" value={item.key} key={item.key}>
                                            <Typography
                                                fontWeight={500}
                                                textTransform={'none'}
                                                color={ownerType === item.key ? '#fff' : undefined}
                                            >
                                                {item.value}
                                            </Typography>
                                        </StyledToggleBtn>
                                    );
                                })}
                            </ToggleButtonGroup>
                        </Grid>
                    </>
                )}
            </Grid>
            <Grid container xs={12}>
                <Grid xs={12} lg={0.5} minWidth={80}>
                    <Box className="flex w-full justify-center items-center mt-4">
                        <Typography>
                            {salesType === 'contract' && t('mall.classification')}
                            {salesType === 'date' && t('mall.searchPeriod')}
                        </Typography>
                    </Box>
                </Grid>
                {salesType === 'contract' && (
                    <>
                        <Grid xs={12} lg={4}>
                            <FormControl fullWidth sx={{ p: 0, m: 0, ml: 1 }} size="small">
                                <InputLabel sx={{ p: 0, m: 0 }} id="select-contract-label">
                                    {t('mall.selectGroupPurchase')}
                                </InputLabel>
                                <Select
                                    sx={{ p: 0, m: 0 }}
                                    labelId="select-contract-label"
                                    label={t('mall.selectGroupPurchase')}
                                    size="small"
                                    id="select-contract"
                                    value={contractId}
                                    {...register('contractId')}
                                    renderValue={(value) => {
                                        const valueData = find(contractList, (o) => o.contractId === value) || '';
                                        const startTimeSet = new Date(valueData?.startDate).getHours();
                                        const endTimeSet = new Date(valueData?.endDate).getHours();
                                        const check24 = new Date(valueData?.endDate).getMinutes();
                                        const selectStartTimeList = getSelectStartTimeList(t);
                                        const selectEndTimeList = getSelectEndTimeList(t);
                                        const checkStart =
                                            selectStartTimeList.filter(
                                                (time) => time.value === String(startTimeSet || '0'),
                                            )[0]?.label || t('product.am0Hour');
                                        const endValue =
                                            selectEndTimeList.filter(
                                                (time) => time.value === String(endTimeSet || '0'),
                                            )[0]?.label || t('product.pm1159Minute');
                                        const startLabel = `${formatLocaleDate(valueData?.startDate)} ${checkStart}`;
                                        const endLabel = `${formatLocaleDate(valueData?.endDate)} ${check24 === 59 ? t('product.pm1159Minute') : endValue}`;
                                        return (
                                            <>
                                                <Typography>{valueData.productName}</Typography>
                                                <Typography>{`${startLabel} ~ ${endLabel}`}</Typography>
                                            </>
                                        );
                                    }}
                                >
                                    <MenuItem value={''}>{t('mall.allGroupPurchases')}</MenuItem>
                                    <Divider sx={{ width: '100%' }} />
                                    {contractList.map((item, idx) => {
                                        if (!item) return;
                                        const startTimeSet = new Date(item?.startDate).getHours();
                                        const endTimeSet = new Date(item?.endDate).getHours();
                                        const check24 = new Date(item?.endDate).getMinutes();
                                        const selectStartTimeList = getSelectStartTimeList(t);
                                        const selectEndTimeList = getSelectEndTimeList(t);
                                        const checkStart =
                                            selectStartTimeList.filter(
                                                (time) => time.value === String(startTimeSet || '0'),
                                            )[0]?.label || t('product.am0Hour');
                                        const endValue =
                                            selectEndTimeList.filter(
                                                (time) => time.value === String(endTimeSet || '0'),
                                            )[0]?.label || t('product.pm1159Minute');
                                        const startLabel = `${formatLocaleDate(item?.startDate)} ${checkStart}`;
                                        const endLabel = `${formatLocaleDate(item?.endDate)} ${check24 === 59 ? t('product.pm1159Minute') : endValue}`;
                                        return (
                                            <MenuItem
                                                value={item.contractId}
                                                key={item.contractId}
                                                className="flex flex-col gap-2"
                                                sx={{ alignItems: 'start' }}
                                            >
                                                <Typography
                                                    sx={{
                                                        maxWidth: '400px',
                                                        whiteSpace: 'pre-wrap',
                                                        textOverflow: 'ellipsis',
                                                        overflow: 'hidden',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 3,
                                                        WebkitBoxOrient: 'vertical',
                                                    }}
                                                >
                                                    {item.productName}
                                                </Typography>
                                                <Typography>{`${startLabel} - ${endLabel}`}</Typography>
                                                {idx !== contractList.length - 1 && <Divider sx={{ width: '100%' }} />}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Grid>
                    </>
                )}
                {salesType === 'date' && (
                    <>
                        {period === 'DAY' && (
                            <>
                                <Grid xs={12} lg={3}>
                                    <MuiDateRangePicker
                                        label={t('mall.selectDate')}
                                        InputProps={{
                                            endAdornment: !!startDate ? (
                                                <InputAdornment position={'end'}>
                                                    <IconButton
                                                        onClick={(ev) => {
                                                            ev.stopPropagation();
                                                            setValue('startDate', '', { shouldDirty: true });
                                                            setValue('endDate', '', { shouldDirty: true });
                                                        }}
                                                    >
                                                        <Cancel />
                                                    </IconButton>
                                                </InputAdornment>
                                            ) : undefined,
                                        }}
                                        size="small"
                                        view={'dialog'}
                                        value={{
                                            start: startDate || '',
                                            end: endDate || '',
                                        }}
                                        fullWidth
                                        onChange={(range) => {
                                            if (!range || !range?.start || !range?.end) return;
                                            setValue('startDate', range.start, { shouldDirty: true });
                                            setValue('endDate', range.end, { shouldDirty: true });
                                            setValue('staticPeriod', 'custom', { shouldDirty: true });
                                        }}
                                        sx={{ pb: 0 }}
                                    />
                                </Grid>
                                <Grid xs={12} lg={3}>
                                    <ToggleButtonGroup
                                        exclusive
                                        sx={{ mt: 1 }}
                                        fullWidth
                                        color="primary"
                                        size="small"
                                        onChange={(e, v) => {
                                            if (!v) return;
                                            setValue('staticPeriod', v, { shouldDirty: true });
                                            const todayMonth =
                                                new Date().getMonth() + 1 >= 10
                                                    ? new Date().getMonth() + 1
                                                    : `0${new Date().getMonth() + 1}`;
                                            const todayDate =
                                                new Date().getDate() >= 10
                                                    ? new Date().getDate()
                                                    : `0${new Date().getDate()}`;
                                            const today = `${new Date().getFullYear()}-${todayMonth}-${todayDate}`;
                                            const endResult = new Date(
                                                moment(today)
                                                    .add(1, 'days')
                                                    .subtract(1, 'milliseconds')
                                                    .format('YYYY-MM-DDTHH:mm:ss.999Z'),
                                            ).toISOString();
                                            let startResult: any;
                                            if (v === 'yesterday') {
                                                startResult = new Date(
                                                    moment(today).subtract(1, 'days').format('YYYY-MM-DDT00:00:00Z'),
                                                ).toISOString();
                                            } else if (v === 'lastWeek') {
                                                startResult = new Date(
                                                    moment(today).subtract(7, 'days').format('YYYY-MM-DDT00:00:00Z'),
                                                ).toISOString();
                                            } else if (v === 'last2Week') {
                                                startResult = new Date(
                                                    moment(today).subtract(14, 'days').format('YYYY-MM-DDT00:00:00Z'),
                                                ).toISOString();
                                            } else if (v === 'lastMonth') {
                                                startResult = new Date(
                                                    moment(today).subtract(30, 'days').format('YYYY-MM-DDT00:00:00Z'),
                                                ).toISOString();
                                            } else if (v === 'last3Month') {
                                                startResult = new Date(
                                                    moment(today).subtract(90, 'days').format('YYYY-MM-DDT00:00:00Z'),
                                                ).toISOString();
                                            } else if (v === 'last6Month') {
                                                startResult = new Date(
                                                    moment(today).subtract(180, 'days').format('YYYY-MM-DDT00:00:00Z'),
                                                ).toISOString();
                                            } else if (v === 'last12Month') {
                                                startResult = new Date(
                                                    moment(today).subtract(365, 'days').format('YYYY-MM-DDT00:00:00Z'),
                                                ).toISOString();
                                            }
                                            setValue('startDate', startResult, { shouldDirty: true });
                                            setValue('endDate', endResult, { shouldDirty: true });
                                        }}
                                        value={staticPeriod}
                                    >
                                        {staticPeriodList.map((item) => {
                                            return (
                                                <StyledToggleBtn color="primary" value={item.key} key={item.key}>
                                                    <Typography
                                                        fontWeight={500}
                                                        textTransform={'none'}
                                                        color={staticPeriod === item.key ? '#fff' : undefined}
                                                    >
                                                        {item.value}
                                                    </Typography>
                                                </StyledToggleBtn>
                                            );
                                        })}
                                    </ToggleButtonGroup>
                                </Grid>
                            </>
                        )}
                        {period === 'MONTH' && (
                            <>
                                <Grid xs={12} lg={2}>
                                    <FormControl fullWidth sx={{ p: 0, m: 0, mt: 1 }} size="small">
                                        <InputLabel sx={{ p: 0, m: 0 }} id="select-start-month-label">
                                            {t('mall.startMonth')}
                                        </InputLabel>
                                        <Select
                                            sx={{ p: 0, m: 0 }}
                                            labelId="select-start-month-label"
                                            label={t('mall.startMonth')}
                                            size="small"
                                            id="select-start-month-label"
                                            value={startMonth}
                                            {...register('startMonth')}
                                            renderValue={(value) => {
                                                const valueData =
                                                    find(selectableMonthList, (o) => o.value === value)?.label || '';
                                                return <>{valueData}</>;
                                            }}
                                        >
                                            {[...selectableMonthList].reverse().map((item) => {
                                                return (
                                                    <MenuItem value={item.value} key={item.value}>
                                                        {item.label}
                                                    </MenuItem>
                                                );
                                            })}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid xs={12} lg={2}>
                                    <FormControl fullWidth sx={{ p: 0, m: 0, mt: 1 }} size="small">
                                        <InputLabel sx={{ p: 0, m: 0 }} id="select-start-month-label">
                                            {t('mall.endMonth')}
                                        </InputLabel>
                                        <Select
                                            sx={{ p: 0, m: 0 }}
                                            labelId="select-end-month-label"
                                            label={t('mall.endMonth')}
                                            size="small"
                                            id="select-end-month-label"
                                            value={endMonth}
                                            {...register('endMonth')}
                                            renderValue={(value) => {
                                                const valueData =
                                                    find(selectableMonthList, (o) => o.value === value)?.label || '';
                                                return <>{valueData}</>;
                                            }}
                                        >
                                            {[...selectableMonthList].reverse().map((item) => {
                                                return (
                                                    <MenuItem value={item.value} key={item.value}>
                                                        {item.label}
                                                    </MenuItem>
                                                );
                                            })}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </>
                        )}
                    </>
                )}
                <Grid xs={0} lg={salesType === 'contract' ? 6 : period === 'DAY' ? 4 : 6} />
                <Grid xs={12} lg={1} className="flex justify-end items-center">
                    <LoadingButton
                        disabled={!isDirty}
                        isLoading={state === 'loading'}
                        variant={'contained'}
                        onClick={() => submit()}
                        startIcon={<Search />}
                        sx={{ mt: salesType === 'contract' ? undefined : 1 }}
                    >
                        {t('mall.search')}
                    </LoadingButton>
                </Grid>
            </Grid>
        </>
    );
};

export default SalesListFilters;

function generateMonthlyOptions(t: any): { value: string; label: string }[] {
    try {
        const startYear = 2023;
        const startMonth = 1;
        const endDate = new Date(); // 현재 날짜
        const endYear = endDate.getFullYear();
        const endMonth = endDate.getMonth() + 1; // JavaScript의 getMonth()는 0부터 시작하므로 1을 더해줍니다.

        const options = [];
        for (let year = startYear; year <= endYear; year++) {
            // 시작 연도의 시작 월과 끝 연도의 끝 월을 설정합니다.
            const start = year === startYear ? startMonth : 1;
            const end = year === endYear ? endMonth : 12;

            for (let month = start; month <= end; month++) {
                const value = `${year}-${String(month).padStart(2, '0')}`;
                const label = `${year}${t('mall.year')} ${month}${t('mall.month')}`;
                options.push({ value, label });
            }
        }
        return options;
    } catch (error) {
        return [];
    }
}

const basicToday = `${new Date().getFullYear()}-${
    new Date().getMonth() + 1 >= 10 ? new Date().getMonth() + 1 : `0${new Date().getMonth() + 1}`
}`;
const monthStartValue = new Date(moment(basicToday).subtract(1, 'months').format('YYYY-MM'));
export const defaultStartMonth = `${monthStartValue.getFullYear()}-${
    monthStartValue.getMonth() + 1 >= 10 ? monthStartValue.getMonth() + 1 : `0${monthStartValue.getMonth() + 1}`
}`;
const monthEndValue = new Date(moment(basicToday).format('YYYY-MM'));
export const defaultEndMonth = `${monthEndValue.getFullYear()}-${
    monthEndValue.getMonth() + 1 >= 10 ? monthEndValue.getMonth() + 1 : `0${monthEndValue.getMonth() + 1}`
}`;

const basicToday2 = `${new Date().getFullYear()}-${
    new Date().getMonth() + 1 >= 10 ? new Date().getMonth() + 1 : `0${new Date().getMonth() + 1}`
}-${new Date().getDate() >= 10 ? new Date().getDate() : `0${new Date().getDate()}`}`;
export const monthDefaultStart = new Date(
    moment(basicToday2).subtract(30, 'days').format('YYYY-MM-DDT00:00:00Z'),
).toISOString();
export const monthDefaultEnd = new Date(
    moment(basicToday2).add(1, 'days').subtract(1, 'milliseconds').format('YYYY-MM-DDTHH:mm:ss.999Z'),
).toISOString();
